import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProtocolsView } from '@/modules/promotion';
import { ActionIntentsTable, ActionIntentDetailsSidebar } from '@/modules/promoted-blueprints';
import { useWorkflowBlueprints } from '@/hooks';

const TABLE_LABELS = {
  intentHeader: 'Workflow',
  subIntentsHeader: 'Scenarios',
  subIntentsSuffix: 'scenarios',
  enabledHeader: 'Promoted',
  emptyMessage: 'No workflows found.'
};

const SIDEBAR_LABELS = {
  intentLabel: 'Workflow',
  subIntentsTitle: 'Scenarios',
  noSubIntentsText: 'No scenarios for this workflow.'
};

type View = 'protocol' | 'intent';

export default function PromotedWorkflows() {
  const [searchParams] = useSearchParams();
  // A link from back testing names the protocol to open.
  const linkedSopId = searchParams.get('sop');
  const [view, setView] = useState<View>('protocol');
  // A protocol's template opens its blueprint beside the list, whichever view found it. A link from
  // Promote names the blueprint whose unlinked templates need a scenario type, and opens it straight away.
  const [openBlueprintId, setOpenBlueprintId] = useState<string | null>(() => searchParams.get('blueprint'));
  const { data: workflowBlueprints, isLoading: workflowLoading } = useWorkflowBlueprints();

  return (
    <div className='flex h-full'>
      <div className='flex flex-1 flex-col gap-6 overflow-y-auto p-4'>
        <div className='space-y-1'>
          <h1 className='text-base font-semibold'>Promoted Protocols</h1>
          <p className='text-sm text-muted-foreground'>
            Precomputed answers the agent serves directly. Promote a workflow to serve it from the precomputed
            answer; turning it off hands the workflow back to the normal pipeline.
          </p>
        </div>

        <Tabs value={view} onValueChange={(value) => setView(value as View)}>
          <TabsList>
            <TabsTrigger value='protocol'>By protocol</TabsTrigger>
            <TabsTrigger value='intent'>By intent</TabsTrigger>
          </TabsList>
        </Tabs>

        {view === 'protocol' ? (
          <div className='flex min-h-0 flex-1 flex-col gap-3'>
            <p className='rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
              Where each protocol&apos;s scenario types stand. Generating, testing and promoting run from the protocol
              itself: open it and choose <span className='font-medium text-foreground'>Promote scenarios</span> in its ⋯
              menu. A write runs without asking only after enough supervisor approvals in a row.
            </p>
            <ProtocolsView key={linkedSopId ?? ''} initialSelected={linkedSopId} onOpenBlueprint={setOpenBlueprintId} />
          </div>
        ) : (
          <div className='flex flex-col gap-5'>
            <p className='rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
              <span className='font-medium text-foreground'>Workflows</span> = multi-step actions.{' '}
              <span className='font-medium text-foreground'>Promote</span> a workflow to serve it from the
              precomputed answer (skips planning); turning it off hands the workflow back to the normal pipeline.
              Open a row to control each <span className='font-medium text-foreground'>named scenario</span>{' '}
              &mdash; a scenario can auto-send, or go out as a draft for review.
            </p>
            <ActionIntentsTable
              workflowBlueprints={workflowBlueprints ?? []}
              isLoading={workflowLoading}
              onRowClick={(blueprint) => setOpenBlueprintId(blueprint.id)}
              labels={TABLE_LABELS}
            />
          </div>
        )}
      </div>

      {openBlueprintId && (
        <ActionIntentDetailsSidebar
          blueprintId={openBlueprintId}
          onClose={() => setOpenBlueprintId(null)}
          labels={SIDEBAR_LABELS}
        />
      )}
    </div>
  );
}
